import { useMemo, useState } from "react";
import { InitialsAvatar } from "../../components/InitialsAvatar";
import { Link } from "react-router-dom";
import { useResource, formatDate, titleCase } from "../../lib/hooks";
import { FilterBar, matches } from "../../components/FilterBar";
import type { Participant, Referral } from "../../lib/types";
import type { CareCase } from "../Cases";

/**
 * The caseload view: who we are serving, through which episodes of service.
 *
 * The old CRM treated the referral as the participant, which is exactly the
 * conflation this schema undid — so its Caseload screen comes back here as a
 * participant-first roll-up with referrals nested under each person. A viewer
 * account sees referrals but no participant rows (workforce PII is outside its
 * scope), and the page says so instead of pretending the list is empty.
 */
export const Caseload = () => {
    const participants = useResource<Participant[]>("/participants");
    const referrals = useResource<Referral[]>("/referrals");
    const cases = useResource<CareCase[]>("/cases");

    const [search, setSearch] = useState("");
    const [filters, setFilters] = useState<Record<string, string>>({});

    const referralsFor = useMemo(() => {
        const map = new Map<string, Referral[]>();
        for (const referral of referrals.data ?? []) {
            if (!referral.participant_id) continue;
            map.set(referral.participant_id, [...(map.get(referral.participant_id) ?? []), referral]);
        }
        return (id: string) => map.get(id) ?? [];
    }, [referrals.data]);

    const unlinked = (referrals.data ?? []).filter((r) => !r.participant_id);

    // The case files (TC-2026-NNNNN) per participant — the episode layer that
    // sits above referrals. No manual "add caseload": accepting a referral
    // opens its case automatically, and the roll-up assembles itself here.
    const casesFor = useMemo(() => {
        const map = new Map<string, CareCase[]>();
        for (const c of cases.data ?? []) {
            map.set(c.participant_id, [...(map.get(c.participant_id) ?? []), c]);
        }
        return (id: string) => map.get(id) ?? [];
    }, [cases.data]);

    const all = participants.data ?? [];
    const filtered = all.filter((p) =>
        matches(search, p.full_name, p.ndis_number) &&
        (!filters.status || p.status === filters.status)
    );

    if (referrals.error) return <div className="error-banner">{referrals.error}</div>;

    return (
        <div>
            <FilterBar
                search={search} onSearch={setSearch}
                searchPlaceholder="Search caseload…"
                values={filters}
                onChange={(name, value) => setFilters((f) => ({ ...f, [name]: value }))}
                filters={[{
                    name: "status", allLabel: "All Status",
                    options: ["active", "on_hold", "inactive", "discharged"].map((s) => ({ value: s, label: titleCase(s) })),
                }]}
            />

            {all.length === 0 && !participants.loading && (
                <div className="card" style={{ marginBottom: "1rem" }}>
                    <div className="card-body" style={{ color: "var(--muted)", fontSize: "0.85rem" }}>
                        Your role does not include participant records, so the caseload shows
                        referrals only. Ask an administrator if you need the participant view.
                    </div>
                </div>
            )}

            <div style={{ display: "grid", gap: "0.9rem" }}>
                {filtered.map((participant) => {
                    const episodes = referralsFor(participant.id);
                    return (
                        <div key={participant.id} className="card">
                            <div className="card-head">
                                <div style={{ display: "flex", alignItems: "center", gap: "0.7rem" }}>
                                    <InitialsAvatar name={participant.full_name} size={34} />
                                    <div>
                                    <h2>{participant.full_name}</h2>
                                    <p style={{ fontSize: "0.76rem", color: "var(--muted)", marginTop: "0.15rem" }}>
                                        {participant.ndis_number ?? "No NDIS number"}
                                        {participant.service_supports.length > 0 &&
                                            ` · ${participant.service_supports.join(", ")}`}
                                    </p>
                                    </div>
                                </div>
                                <div style={{ display: "flex", gap: "0.35rem", alignItems: "center", flexWrap: "wrap" }}>
                                    {casesFor(participant.id).map((c) => (
                                        <Link key={c.id} to="/cases" className={`chip mono ${c.status === "open" ? "pass" : c.status === "on_hold" ? "warn" : "mute"}`}
                                            title={`Case ${c.ref_code ?? ""} — ${titleCase(c.status)} · opened ${formatDate(c.opened_on)}`}
                                            style={{ fontWeight: 700, textDecoration: "none" }}>
                                            {c.ref_code ?? "case"}
                                        </Link>
                                    ))}
                                    <span className={`chip ${participant.status === "active" ? "pass" : "mute"}`}>
                                        {titleCase(participant.status)}
                                    </span>
                                </div>
                            </div>
                            <div className="card-body flush">
                                <div className="table-wrap">
                                    <table>
                                        <tbody>
                                            {episodes.map((referral) => (
                                                <tr key={referral.id}>
                                                    <td className="mono">{formatDate(referral.received_at)}</td>
                                                    <td>{referral.referring_organisation ?? referral.referring_contact_name}</td>
                                                    <td>
                                                        {referral.services.map((service) => (
                                                            <span key={service} className="chip info" style={{ marginRight: 3 }}>{service}</span>
                                                        ))}
                                                    </td>
                                                    <td>
                                                        <span className={`chip ${referral.status === "active" || referral.status === "allocated" ? "pass" : referral.status === "new" ? "warn" : "mute"}`}>
                                                            {titleCase(referral.status)}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                            {episodes.length === 0 && (
                                                <tr><td className="empty">No referrals on file for this participant.</td></tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {unlinked.length > 0 && (
                <div className="card" style={{ marginTop: "0.9rem" }}>
                    <div className="card-head">
                        <h2>Referrals not yet linked to a participant</h2>
                        <span className="chip warn">{unlinked.length}</span>
                    </div>
                    <div className="card-body flush">
                        <div className="table-wrap">
                            <table>
                                <tbody>
                                    {unlinked.map((referral) => (
                                        <tr key={referral.id}>
                                            <td className="mono">{formatDate(referral.received_at)}</td>
                                            <td style={{ fontWeight: 600 }}>{referral.referring_contact_name}</td>
                                            <td>{referral.referring_organisation ?? "—"}</td>
                                            <td><span className="chip warn">{titleCase(referral.status)}</span></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <div className="card-body" style={{ borderTop: "1px solid var(--line-soft)", fontSize: "0.8rem", color: "var(--muted)" }}>
                        A referral cannot be accepted until it is linked to a participant record —{" "}
                        <Link to="/referrals">link them from Referrals</Link>.
                    </div>
                </div>
            )}
        </div>
    );
};
